import type { ProviderDetail } from '@katoa/schema-provider'
import { medicalSpecialtiesFor, medicalSpecialtyUrl } from './medical-specialty'
import {
  absoluteUrl,
  ORGANIZATION_LEGAL_NAME,
  ORGANIZATION_NAME,
  ORGANIZATION_URL,
  SITE_ALTERNATE_NAME,
  SITE_NAME,
  SITE_ORIGIN,
} from './site'

// The JSON-LD graph shapes vit.al emits (VIT-17746). Pure builders — no router,
// no request, no I/O — so every guard below is unit-testable against a fixture.
//
// Route-level nodes are handed to React Router's own `{ 'script:ld+json': … }`
// meta descriptor, which serialises AND escapes them (`&`, `<`, `>`, U+2028,
// U+2029 → \u-sequences), so a provider whose name contained `</script>` cannot
// break out of the tag. That is why there is no stringify or escape helper here:
// a second serialisation path would be a second thing to get wrong. The only
// exception is the site graph, which root.tsx renders directly — see
// StructuredData in app/components/structured-data.tsx.

type JsonLdValue = string | number | boolean | null | JsonLdValue[] | { [key: string]: JsonLdValue }

export type JsonLdObject = { [key: string]: JsonLdValue }

/**
 * Build an object from entries, dropping every null/undefined one.
 *
 * The load-bearing guard in this file. Google reads an empty or invented field as
 * a data-quality problem rather than as an absence, and the provider endpoint
 * answers off whichever sources replied (`provider.sources` reports the rest), so
 * a degraded read has to emit FEWER properties, not blank ones. Making that
 * structural means every builder below can compute a value or `null` and never
 * has to remember a conditional spread.
 */
const compact = (entries: Record<string, JsonLdValue | null | undefined>): JsonLdObject =>
  Object.fromEntries(Object.entries(entries).filter(([, value]) => value != null)) as JsonLdObject

/** Trimmed value, or null when absent or whitespace-only. Mirrors provider.tsx's `isNonEmpty`. */
const trimmed = (value: string | null | undefined): string | null => {
  const next = value?.trim() ?? ''
  return next === '' ? null : next
}

/** A non-empty list, or null so `compact` drops the property entirely. */
const nonEmpty = (values: JsonLdObject[]): JsonLdObject[] | null => (values.length > 0 ? values : null)

// Stable node ids so the two site-wide nodes can reference each other, and so a
// consumer merging graphs across pages recognises them as the same entities.
const WEBSITE_ID = `${SITE_ORIGIN}/#website`
const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`

/**
 * The site-wide `WebSite` + `Organization` pair, emitted on every page.
 *
 * This is the fix for the SERP showing the bare `vit.al` domain as the site name:
 * Google derives that from `WebSite.name`, and with no structured data at all it
 * fell back to the hostname. `alternateName` offers the short form as a
 * fallback for when the full one doesn't fit.
 *
 * No `SearchAction` / sitelinks-searchbox node: Google deprecated that markup in
 * November 2024 and no longer uses it.
 *
 * @param logoUrl Absolute URL of the wordmark — passed in rather than imported so
 * this module stays free of Vite asset imports and its test needs no asset stub.
 */
export const siteGraph = (logoUrl: string): JsonLdObject => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      name: SITE_NAME,
      alternateName: SITE_ALTERNATE_NAME,
      url: `${SITE_ORIGIN}/`,
      inLanguage: 'en-US',
      publisher: { '@id': ORGANIZATION_ID },
    },
    {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: ORGANIZATION_NAME,
      legalName: ORGANIZATION_LEGAL_NAME,
      // The company's own site, which is a different thing from the product site
      // the WebSite node above describes. No `sameAs`: it exists to disambiguate
      // an entity across profiles, and the only other URL we could list is this
      // same one.
      url: ORGANIZATION_URL,
      logo: { '@type': 'ImageObject', url: logoUrl },
    },
  ],
})

/**
 * One rung of a breadcrumb trail. `path` is omitted on the last rung only — it is
 * the page being viewed, and Google requires `item` on every rung except that one.
 */
export type BreadcrumbRung = { name: string; path?: string | undefined }

/**
 * `BreadcrumbList` for the deep route types, so the SERP renders
 * `Vital Care Finder > Cardiologists in Portland, OR > Jane Doe` in place of the
 * raw URL line.
 *
 * Rungs are passed in rather than derived from the URL here: the grammar is
 * hierarchical (docs/url-structure.md) but the display strings are not in it —
 * `portland--or` has to become "Portland, OR" and `cardiologist` "Cardiologists",
 * both of which the routes already have to hand from their loaders.
 */
export const breadcrumbList = (rungs: BreadcrumbRung[]): JsonLdObject => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  // Named for where the trail ends, which is the page it describes. Presentation
  // only — Google's breadcrumb spec doesn't ask for it — but a BreadcrumbList
  // carries no name of its own, so tools that title a node by `name` (the Rich
  // Results Test labels the card "Unnamed item") have nothing to show without it.
  name: rungs[rungs.length - 1]?.name ?? SITE_NAME,
  itemListElement: rungs.map((rung, index) =>
    compact({
      '@type': 'ListItem',
      position: index + 1,
      name: rung.name,
      // A typed node rather than the bare URL string the spec also allows: both
      // are accepted, but a plain string leaves a consumer to infer the referent,
      // and the Rich Results Test renders it as an untyped `Thing`. These rungs
      // are pages, so they are WebPages.
      item: rung.path != null ? { '@type': 'WebPage', '@id': absoluteUrl(rung.path) } : null,
    }),
  ),
})

/** One entry of a results-page `ItemList` — a rendered provider card. */
export type ItemListEntry = { name: string; path: string }

/**
 * `ItemList` over the provider cards a results page actually rendered.
 *
 * Marks the city × specialty matrix as curated, ordered result sets rather than
 * as thin near-duplicate pages. Built from the filtered list the grid maps over,
 * never from the raw recommendation count, so the markup cannot claim more
 * results than the page shows — the same invariant ResultsHeading holds.
 */
export const itemList = (name: string, entries: ItemListEntry[]): JsonLdObject => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  numberOfItems: entries.length,
  itemListElement: entries.map((entry, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: entry.name,
    url: absoluteUrl(entry.path),
  })),
})

/** `PostalAddress`, or null when the endpoint resolved no part of an address. */
const postalAddress = (address: ProviderDetail['address']): JsonLdObject | null => {
  if (address == null) return null

  const node = compact({
    '@type': 'PostalAddress',
    streetAddress: [trimmed(address.line1), trimmed(address.line2)].filter((line) => line != null).join(', ') || null,
    addressLocality: trimmed(address.city),
    addressRegion: trimmed(address.state),
    postalCode: trimmed(address.postalCode),
    addressCountry: 'US',
  })

  // `@type` and the hardcoded country are always there, so an address the
  // endpoint knew nothing about would otherwise emit a node asserting only that
  // this provider is somewhere in the United States.
  return Object.keys(node).length > 2 ? node : null
}

/**
 * `AggregateRating`, or null.
 *
 * Emitted only when BOTH the average and a non-zero count are present, which is
 * exactly when the provider page renders the visible rating row — Google treats
 * an `AggregateRating` with no on-page equivalent as a policy violation, and a
 * `ratingCount` of 0 is invalid markup besides.
 */
const aggregateRating = (ratingsAvg: number | null, ratingsCount: number | null): JsonLdObject | null =>
  ratingsAvg == null || ratingsCount == null || ratingsCount <= 0
    ? null
    : {
        '@type': 'AggregateRating',
        // Passed through, NOT rescaled: the endpoint already rescales Ribbon's
        // 0-10 to 0-5 so the value matches what the search card renders (see
        // providerDetailSchema's comment on `ratingsAvg`). Dividing again here
        // would halve every rating on the site.
        ratingValue: ratingsAvg,
        ratingCount: ratingsCount,
        // Stated rather than left to schema.org's defaults (1-5), because the
        // scale really is 0-5 — a rescaled Ribbon 0 lands at 0, not 1.
        bestRating: 5,
        worstRating: 0,
      }

/**
 * `medicalSpecialty` as enumeration members, or null when nothing maps.
 *
 * The property's range is https://schema.org/MedicalSpecialty, so the endpoint's
 * display text cannot go in it directly — see app/medical-specialty.ts for the
 * mapping and for why an unmapped specialty emits nothing rather than the raw
 * string. One member is emitted bare, several as an array: a display string like
 * `Psychiatry & Neurology` names two fields and both are true.
 */
const medicalSpecialty = (specialty: string | null): JsonLdValue | null => {
  const members = medicalSpecialtiesFor(specialty).map(medicalSpecialtyUrl)
  if (members.length === 0) return null
  return members.length === 1 ? (members[0] ?? null) : members
}

/**
 * `Physician` for a provider detail page (VIT-17746).
 *
 * Every property is driven by the loader's record and guarded through `compact`,
 * so a thin or degraded read (`provider.sources` reporting `miss`/`error`) emits
 * a smaller node rather than one with empty fields. Nothing here is invented, and
 * nothing is marked up that the page does not render — the fields map one-to-one
 * onto the hero, the contact card, and the education/certification sections.
 *
 * Years are deliberately absent from the credential nodes: `startYear` and an
 * education `year` are bare years, and the schema.org properties that would carry
 * them (`validFrom`, `dateCreated`) are dates. The names and the awarding board
 * are the parts a consumer can use.
 */
export const physician = (provider: ProviderDetail, canonicalPath: string): JsonLdObject => {
  const url = absoluteUrl(canonicalPath)

  return compact({
    '@context': 'https://schema.org',
    // Both types, and the order matters only for readability. The page is about an
    // individual practitioner, but schema.org's `Physician` descends from
    // MedicalOrganization — so `alumniOf`, which is a Person property, is not
    // valid on `Physician` alone (validator.schema.org: UNKNOWN_FIELD). Typing the
    // node as both keeps every organisation-side property valid — `aggregateRating`
    // and `parentOrganization` are Organization/Place properties a bare Person
    // could not carry — while legitimising the education roster below.
    '@type': ['Person', 'Physician'],
    '@id': url,
    url,
    name: provider.name,
    medicalSpecialty: medicalSpecialty(provider.specialty),
    image: trimmed(provider.headshotUrl),
    telephone: trimmed(provider.phone),
    address: postalAddress(provider.address),
    // The NPI as a typed identifier rather than a bare string, so a consumer can
    // tell what the number is. This is the page's only globally-unique key.
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'NPI',
      value: provider.npi,
    },
    aggregateRating: aggregateRating(provider.ratingsAvg, provider.ratingsCount),
    // The clinic the contact card names. `facilityName` is `locations[0]`'s, which
    // is the place the page describes; `organizationName` is the provider's own
    // employer and the fallback the sidebar already uses.
    parentOrganization: ((): JsonLdObject | null => {
      const name = trimmed(provider.facilityName) ?? trimmed(provider.organizationName)
      return name == null ? null : { '@type': 'MedicalOrganization', name }
    })(),
    // Both rosters drop an entry whose name is blank rather than emitting a node
    // with an empty one: `compact` filters null/undefined but keeps `''`, and the
    // contract types these names `string` (not nullable), so nothing upstream of
    // here stops a whitespace-only value. The producer does filter them today
    // (`isNonEmpty` in apps/providers/src/core/provider-detail.ts), but that is a
    // separate service — and an EducationalOrganization or a credential with no
    // name asserts nothing a consumer can use.
    alumniOf: nonEmpty(
      provider.educations.flatMap((education): JsonLdObject[] => {
        const name = trimmed(education.name)
        return name == null ? [] : [{ '@type': 'EducationalOrganization', name }]
      }),
    ),
    hasCredential: nonEmpty(
      provider.certifications.flatMap((certification): JsonLdObject[] => {
        const name = trimmed(certification.name)
        if (name == null) return []

        const board = trimmed(certification.board)

        return [
          compact({
            '@type': 'EducationalOccupationalCredential',
            name,
            credentialCategory: 'Board certification',
            recognizedBy: board == null ? null : { '@type': 'Organization', name: board },
          }),
        ]
      }),
    ),
  })
}
